'use client';

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Link from 'next/link';
import { useAppStore } from '@/store/app';
import { api } from '@/lib/api';
import CoinLogo from '@/components/CoinLogo';

interface SectorInfo {
  id: string;
  name: string;
  nameZh: string | null;
  marketCapUsd: number | null;
  change24h: number | null;
  topCoins: string[];
  topCoinLogos: string[];
}

interface SectorCoin {
  symbol: string;
  name: string;
  price: number | null;
  change24h: number | null;
  marketCapUsd: number | null;
  weightPct: number | null;
}

interface SectorDetail {
  coins: SectorCoin[];
  count: number;
  upCount: number;
  downCount: number;
  updatedAt: number;
  weightBasis: string;
}

function LogoImg({ src, symbol }: { src: string; symbol: string }) {
  const [err, setErr] = useState(false);
  if (err) return <CoinLogo symbol={`${symbol}USDT`} size={22} />;
  // eslint-disable-next-line @next/next/no-img-element
  return <img src={src} alt="" width={22} height={22} onError={() => setErr(true)} className="h-[22px] w-[22px] rounded-full bg-gf-surface2 object-contain" />;
}

function fmt(n: number | null | undefined): string {
  if (n == null) return '—';
  if (n >= 1e12) return '$' + (n / 1e12).toFixed(2) + 'T';
  if (n >= 1e9) return '$' + (n / 1e9).toFixed(2) + 'B';
  if (n >= 1e6) return '$' + (n / 1e6).toFixed(1) + 'M';
  return '$' + n.toLocaleString();
}

export default function SectorsView() {
  const { language } = useAppStore();
  const isZh = language !== 'en';
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data: sectors, isLoading } = useQuery({
    queryKey: ['sectors'],
    queryFn: () => api.get<SectorInfo[]>('/market-extra/sectors'),
    refetchInterval: 300_000,
    staleTime: 120_000,
  });

  const { data: sectorDetail, isFetching: loadingCoins } = useQuery({
    queryKey: ['sector-detail', expanded],
    queryFn: () => api.get<SectorDetail>(`/market-extra/sectors/${expanded}/detail`),
    enabled: !!expanded,
    staleTime: 60_000,
  });

  const list = sectors || [];

  return (
    <div>
      {isLoading && !list.length ? (
        <div className="py-10 text-center text-sm text-gf-muted">{isZh ? '加载中...' : 'Loading...'}</div>
      ) : (
        <div className="space-y-2">
          {list.map((s, idx) => {
            const isOpen = expanded === s.id;
            const coins = sectorDetail?.coins || [];
            return (
              <div key={s.id} className="card overflow-hidden !p-0">
                <button
                  onClick={() => setExpanded(isOpen ? null : s.id)}
                  className="flex w-full items-center gap-3 px-3 py-2.5 text-left hover:bg-gf-surface2/60"
                >
                  <span className="w-6 shrink-0 text-center font-mono text-xs text-gf-muted">{idx + 1}</span>
                  <span className="flex w-20 shrink-0">
                    {(s.topCoinLogos.length
                      ? s.topCoinLogos
                      : s.topCoins.map((c) => c))
                      .slice(0, 3)
                      .map((src, i) =>
                        String(src).startsWith('http') ? (
                          <span key={src + i} className={i > 0 ? '-ml-2' : ''}>
                            <LogoImg src={src} symbol={String(s.topCoins[i] ?? '')} />
                          </span>
                        ) : (
                          <span key={src + i} className={i > 0 ? '-ml-2' : ''}>
                            <CoinLogo symbol={`${src}USDT`} size={22} />
                          </span>
                        ),
                      )}
                  </span>
                  <span className="flex min-w-0 flex-1 flex-col">
                    <span className="truncate text-sm font-medium">{isZh && s.nameZh ? s.nameZh : s.name}</span>
                    {/* AUD16: keep market cap visible on mobile */}
                    {isZh && s.nameZh && s.nameZh !== s.name ? (
                      <span className="truncate text-[10px] text-gf-muted">{s.name} · {fmt(s.marketCapUsd)}</span>
                    ) : (
                      <span className="truncate text-[10px] text-gf-muted sm:hidden">{fmt(s.marketCapUsd)}</span>
                    )}
                  </span>
                  <span className="hidden font-mono text-xs text-gf-muted sm:block">{fmt(s.marketCapUsd)}</span>
                  <span className={`w-16 shrink-0 text-right font-mono text-xs ${(s.change24h ?? 0) >= 0 ? 'text-gf-positive' : 'text-gf-negative'}`}>
                    {(s.change24h ?? 0) >= 0 ? '+' : ''}{(s.change24h ?? 0).toFixed(2)}%
                  </span>
                  <span className={`text-xs transition-transform ${isOpen ? 'rotate-90' : ''}`}>›</span>
                </button>
                {isOpen && (
                  <div className="border-t border-gf-border/60 px-3 py-2">
                    {/* D17: constituent meta — count / breadth / weight basis / freshness */}
                    {sectorDetail && (
                      <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] text-gf-muted">
                        <span>
                          {isZh ? '成分' : 'Members'}: <span className="font-medium text-gf-text">{sectorDetail.count}</span>
                        </span>
                        <span className="text-gf-positive">
                          {isZh ? '上涨' : 'Up'}: {sectorDetail.upCount}
                        </span>
                        <span className="text-gf-negative">
                          {isZh ? '下跌' : 'Down'}: {sectorDetail.downCount}
                        </span>
                        <span>
                          {isZh ? '权重' : 'Weight'}: {isZh ? '市值加权' : 'Market-cap weighted'}
                        </span>
                        <span>
                          {isZh ? '更新' : 'Updated'}:{' '}
                          {new Date(sectorDetail.updatedAt).toLocaleTimeString(isZh ? 'zh-TW' : 'en-US', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    )}
                    {loadingCoins && !coins.length ? (
                      <div className="py-4 text-center text-xs text-gf-muted">{isZh ? '加载币种...' : 'Loading coins...'}</div>
                    ) : !coins.length ? (
                      <div className="py-4 text-center text-xs text-gf-muted">
                        {isZh ? '该分类暂无币种数据' : 'No coins in this category'}
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 gap-1 md:grid-cols-3">
                        {coins.map((c) => (
                          <Link
                            key={c.symbol}
                            // D17: members without a platform market go to the asset info page, never a fabricated perp link.
                            href={`/coin/${c.symbol}`}
                            className="flex items-center gap-2 rounded-lg bg-gf-surface2/50 px-2 py-1.5 hover:bg-gf-surface2"
                          >
                            <CoinLogo symbol={`${c.symbol}USDT`} size={20} />
                            <span className="min-w-0 flex-1 truncate text-xs">
                              <span className="font-medium">{c.symbol}</span>
                              <span className="ml-1 text-[9px] text-gf-muted">{c.name}</span>
                            </span>
                            {c.weightPct != null && (
                              <span className="shrink-0 font-mono text-[9px] text-gf-muted">{c.weightPct.toFixed(1)}%</span>
                            )}
                            <span className={`shrink-0 font-mono text-[10px] ${(c.change24h ?? 0) >= 0 ? 'text-gf-positive' : 'text-gf-negative'}`}>
                              {(c.change24h ?? 0) >= 0 ? '+' : ''}{(c.change24h ?? 0).toFixed(1)}%
                            </span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <div className="mt-3 text-[10px] text-gf-muted">
        {isZh
          ? '数据来源：CoinGecko 免费分类接口 · 每 5 分钟更新 · 涨幅为供应商市值加权口径，重叠板块不直接求和'
          : 'Source: CoinGecko free categories · refreshed every 5 min · change is supplier market-cap weighted; overlapping sectors are not summed'}
      </div>
    </div>
  );
}
